import { useState, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ProjectScope } from '@/lib/schemas';
import type { ScopeContact } from '@/components/projects/ShareWithClientDialog';

interface DeliverableScopePortalPromptProps {
  /** Deliverable ID for session storage key */
  deliverableId: string;
  /** Scope information */
  scope: ProjectScope | undefined;
  /** Whether a portal already exists for this scope's org */
  portalExists: boolean;
  /** Organization name to display */
  orgName: string;
  /** Project ID for dialog pre-fill */
  projectId: string;
  /** Callback when "Set Up Portal" or "Manage Portal" is clicked */
  onOpenDialog: (scopeContacts?: ScopeContact[]) => void;
  /** Whether the hook is still loading */
  isLoading: boolean;
}

/**
 * Renders a prompt banner for external scopes without a portal, or a link if a portal exists.
 *
 * For internal scopes (scope_type_slug === 'self'), renders nothing.
 *
 * Dismiss state persists in sessionStorage for the current page load.
 */
export function DeliverableScopePortalPrompt({
  deliverableId,
  scope,
  portalExists,
  orgName,
  onOpenDialog,
  isLoading,
}: DeliverableScopePortalPromptProps) {
  // Skip if internal scope or no scope data yet
  if (!scope || scope.scope_type_slug === 'self' || isLoading) {
    return null;
  }

  // Session storage key for dismiss state
  const dismissKey = `deliverable-portal-prompt-${deliverableId}`;
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check sessionStorage on mount
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(dismissKey) === 'true';
    }
    return false;
  });

  // Update isDismissed when deliverableId changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const dismissed = sessionStorage.getItem(dismissKey) === 'true';
      setIsDismissed(dismissed);
    }
  }, [dismissKey]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(dismissKey, 'true');
    }
  };

  const handleOpenDialog = () => {
    // Pre-fill with scope contacts
    const scopeContacts = scope?.contacts?.map(c => ({
      contact_email: c.contact_email,
      contact_name: c.contact_name,
      organization_name: scope.organization_name || c.organization_name,
      is_client_type: scope.is_client_type,
    })) || [];
    onOpenDialog(scopeContacts);
  };

  // If dismissed, don't render
  if (isDismissed) {
    return null;
  }

  // Portal exists: show "Manage" link
  if (portalExists) {
    return (
      <div className="px-3 py-2 flex items-center gap-2 text-sm text-muted-foreground border-t">
        <Globe className="h-3.5 w-3.5" />
        <span>
          <button
            onClick={handleOpenDialog}
            className="text-blue-600 hover:text-blue-700 underline"
          >
            Manage {orgName} portal
          </button>
        </span>
      </div>
    );
  }

  // No portal: show "Set Up" banner with dismiss
  return (
    <div className="px-3 py-2.5 flex items-center justify-between gap-2 border-t bg-blue-50/50 border-blue-100">
      <div className="flex items-center gap-2 text-sm">
        <Globe className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-foreground">
          This deliverable is assigned to <strong>{orgName}</strong>.{' '}
          <button
            onClick={handleOpenDialog}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Set Up Portal →
          </button>
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-5 w-5 p-0 hover:bg-blue-200/50 shrink-0"
        onClick={handleDismiss}
        title="Dismiss this banner"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3 text-muted-foreground" />
      </Button>
    </div>
  );
}
